import { User, LogOut, FolderOpen, Shield, Calendar, Settings } from 'lucide-react';
import useStore from '../store/useStore';
import { Button } from '@/ui/button';
import NotificationBell from './NotificationBell';
import ThemeToggle from './ThemeToggle';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/ui/dropdown-menu';

const AuthHeader = ({ showMyProjects = true, showBookCustomize = false }) => {
  // Individual selectors → header only re-renders when these specific values change
  const currentUser = useStore((s) => s.currentUser);
  const setAuthModal = useStore((s) => s.setAuthModal);
  const signOut = useStore((s) => s.signOut);
  const setCurrentPage = useStore((s) => s.setCurrentPage);
  const setBookWithoutDesign = useStore((s) => s.setBookWithoutDesign);

  const handleMyProjects = () => {
    setCurrentPage('saved-designs');
  };

  const handleAdminPanel = () => {
    setCurrentPage('admin');
  };

  const handleSuperAdminPanel = () => {
    setCurrentPage('super-admin');
  };

  const handleBookCustomize = () => {
    setBookWithoutDesign(true);  // Mark this as a "Book & Customize" flow
    setCurrentPage('booking');
  };

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'flat_admin';
  const isSuperAdmin = currentUser?.role === 'super_admin';

  if (currentUser) {
    return (
      <div className="flex items-center gap-1 sm:gap-3">
        {/* Theme Toggle */}
        <ThemeToggle variant="button" />
        
        {showBookCustomize && (
          <Button
            onClick={handleBookCustomize}
            variant="outline"
            size="sm"
            className="border-amber-500 text-amber-400 hover:bg-amber-500/10 px-2 sm:px-4"
            data-testid="book-customize-header-btn"
          >
            <Calendar className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Book</span>
          </Button>
        )}
        
        {/* Notification Bell */}
        <NotificationBell />
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-300 hover:text-white px-2 sm:px-4"
              data-testid="user-menu-btn"
            >
              <User className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{currentUser.displayName}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
            <DropdownMenuItem 
              onClick={handleMyProjects}
              className="text-slate-300 focus:bg-slate-700"
            >
              <FolderOpen className="h-4 w-4 mr-2" />
              My Projects
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => setCurrentPage('my-account')}
              className="text-slate-300 focus:bg-slate-700"
              data-testid="my-account-menu-item"
            >
              <Settings className="h-4 w-4 mr-2" />
              My Account
            </DropdownMenuItem>
            {isAdmin && (
              <DropdownMenuItem 
                onClick={handleAdminPanel}
                className="text-amber-400 focus:bg-slate-700"
              >
                <Shield className="h-4 w-4 mr-2" />
                Admin Panel
              </DropdownMenuItem>
            )}
            {isSuperAdmin && (
              <DropdownMenuItem
                onClick={handleSuperAdminPanel}
                className="text-amber-300 focus:bg-slate-700"
                data-testid="super-admin-menu-item"
              >
                <Shield className="h-4 w-4 mr-2" />
                Super Admin
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator className="bg-slate-700" />
            <DropdownMenuItem 
              onClick={signOut}
              className="text-slate-300 focus:bg-slate-700"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 sm:gap-2">
      {/* Theme Toggle for non-logged in users */}
      <ThemeToggle variant="button" />
      
      {showBookCustomize && (
        <Button
          onClick={handleBookCustomize}
          variant="outline"
          size="sm"
          className="border-amber-500 text-amber-400 hover:bg-amber-500/10 px-2 sm:px-4"
          data-testid="book-customize-header-btn"
        >
          <Calendar className="h-4 w-4 md:mr-2" />
          <span className="hidden md:inline">Book</span>
        </Button>
      )}
      <Button
        onClick={() => setAuthModal('signin')}
        variant="ghost"
        size="sm"
        className="text-slate-300 hover:text-white px-2 sm:px-4"
        data-testid="header-signin-btn"
      >
        Sign In
      </Button>
      <Button
        onClick={() => setAuthModal('signup')}
        variant="outline"
        size="sm"
        className="border-teal-500 text-teal-400 hover:bg-teal-500/10 px-2 sm:px-4"
        data-testid="header-signup-btn"
      >
        <span className="hidden sm:inline">Sign Up</span>
        <span className="sm:hidden">Join</span>
      </Button>
    </div>
  );
};

export default AuthHeader;
